export interface BnplProductInfo {
  key: string;
  label: string;
  logo: string;
  title: string;
  subTitle?: string;
  description?: string;
  steps?: string[];
  legal?: string[];
}

export type BnplLearnMoreSite = 'ca' | 'uk' | 'fr' | 'de';

export const CA_BNPL_PRODUCT_INFO: Record<string, BnplProductInfo> = {
  paypal: {
    key: 'paypal',
    label: 'PayPal',
    logo: '/payment/PC_paypal.png',
    title: 'Pay in 4 interest-free payments',
    legal: [
      'Pay in 4 is available to approved consumers for purchases of $30 to $1,500 (CAD) for certain transaction types. Availability depends on the merchant. You must be age of majority in the province or territory where you live to apply. Any transactions in other currencies will be converted to Canadian dollars subject to applicable currency conversion charges. You may repay the amount you owe in full at any time, without any fees or charges.',
    ],
  },
  klarna: {
    key: 'klarna',
    label: 'Klarna',
    logo: '/payment/PC_klarna.png',
    title: 'interest-free payments of',
    subTitle: "Buy what you love and split the cost. It's easy and interest-free.<br><br>",
    steps: [
      'Add item(s) to your cart',
      'Go to checkout and choose Klarna.',
      'Enter your debit or credit card information',
      'Your first payment is taken when the order is processed and the remaining 3 are automatically taken every two weeks',
    ],
    legal: [
      "See payment <a style='text-decoration: underline;' href='https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_us/sliceitinx' target='_blank'>terms</a>. A higher initial payment may be required for some consumers.",
      'CA residents: Loans made or arranged pursuant to a California Finance Lenders Law license.',
    ],
  },
  afterpay: {
    key: 'afterpay',
    label: 'Afterpay',
    logo: '/payment/PC_afterpay.png',
    title: 'Shop now. Pay later.Always interest-free',
    description: '<br>',
    steps: [
      'Add your favorites to cart',
      'Select Afterpay at checkout',
      'Log into or create your Afterpay account, instant approval decision',
      'Your purchase will be split into 4 payments, payable every 2 weeks',
    ],
    legal: [
      `You must be over 18, a resident of the U.S. and meet additional eligibility criteria to qualify. Late fees may apply. Estimated payment amounts shown on product pages exclude taxes and shipping charges, which are added at checkout. Click here for complete terms. Loans to California residents made or arranged pursuant to a California Finance Lenders Law license.`,
    ],
  },
};

export const UK_BNPL_PRODUCT_INFO: Record<string, BnplProductInfo> = {
  paypal: {
    key: 'paypal',
    label: 'PayPal',
    logo: '/payment/PC_paypal.png',
    title: 'Pay in 3 interest-free payments',
    legal: [
      'Pay in 3 is an unregulated credit agreement, so you will have fewer protections under this agreement than you would under a regulated credit agreement. Carefully consider whether the purchase is affordable and how you will make the repayments. Be aware of the possible impact of using Pay in 3 and of missing payments, including making other borrowing more difficult or more expensive. Pay in 3 eligibility is subject to status and approval. 18+ UK residents only. Available for purchases of £20 to £3,000. Terms and conditions apply. PayPal Pay in 3 is a trading name of PayPal UK Ltd, 5 Fleet Place, London, United Kingdom, EC4M 7RD.',
    ],
  },
  klarna: {
    key: 'klarna',
    label: 'Klarna',
    logo: '/payment/PC_klarna.png',
    title: 'interest-free payments of',
    subTitle: "Buy what you love and split the cost. It's easy and interest-free.<br><br>",
    steps: [
      'Add item(s) to your cart',
      'Go to checkout and choose Klarna.',
      'Enter your debit or credit card information',
      'Your first payment is taken when the order is processed and the  remaining 2 are automatically taken every 30 days',
    ],
    legal: [
      "See payment <a style='text-decoration: underline;' href='https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_gb/paylaterin3' target='_blank'>terms</a>. A higher initial payment may be required for some consumers.",
      'CA residents: Loans made or arranged pursuant to a California Finance Lenders Law license.',
    ],
  },
  clearpay: {
    key: 'clearpay',
    label: 'Clearpay',
    logo: '/payment/PC_clearpay.png',
    title: 'Shop now. Pay later.Always interest-free.',
    description: '<br>',
    steps: [
      'Add your favourites to cart',
      'Select Clearpay at checkout',
      'Log into or create your Clearpay account, instant approval decision',
      'Your purchase will be split into 4 payments, payable every 2 weeks',
    ],
    legal: [
      "Clearpay lends you a fixed amount of credit so you can pay for your purchase over 4 instalments, due every 2 weeks.Ensure you can make repayments on time. You must be 18+ and a permanent UK resident (excl Channel Islands).Clearpay charges a £6 late fee for each late instalment and a further £6 if it's still unpaid 7 days later. Late fees are capped at £6 for orders under £24 and the lower of £24 or 25% of the order value for orders over £24. Missed payments may affect your ability to use Clearpay in the future and your details may be passed onto a debt collection agency working on Clearpay's behalf. Clearpay is credit that is not regulated by the Financial Conduct Authority. T&Cs and other eligibility criteria apply at <a style='text-decoration: underline;' href='https://clearpay.co.uk/terms' target='_blank'>clearpay.co.uk/terms</a>",
    ],
  },
};

export const FR_BNPL_PRODUCT_INFO: Record<string, BnplProductInfo> = {
  paypal: {
    key: 'paypal',
    label: 'PayPal',
    logo: '/payment/PC_paypal.png',
    title: 'Payez en 4X sans frais',
    legal: [
      "Uniquement pour les particuliers résidant en France. Compte PayPal français obligatoire. Le Paiement en 4X est disponible sous réserve d'acceptation, pour les achats de 20 € à 3 000 €. Un crédit est un engagement contraignant et doit être remboursé, vérifiez votre capacité de remboursement. Voir les <a style='text-decoration: underline;' href='https://www.paypal.com/fr/webapps/mpp/ua/pay-in-4x-full' target='_blank'>Conditions générales</a> du Paiement en 4X. Crédit proposé par PayPal (Europe) S.à r.l. et Cie, S.C.A., 22-24 Boulevard Royal, L-2449, Luxembourg, R.C.S. Luxembourg B 118 349, qui est dûment licencié en tant qu'établissement de crédit du Luxembourg et est sous le contrôle prudentiel de l'autorité de surveillance du Luxembourg, la Commission de Surveillance du Secteur Financier. Cette information est fournie par PayPal.",
    ],
  },
  klarna: {
    key: 'klarna',
    label: 'Klarna',
    logo: '/payment/PC_klarna.png',
    title: "Payez à votre rythme, dès 0 % d'intérêt",
    subTitle: 'Lorsque vous faites des achats et payez avec Klarna, le montant que vous',
    description:
      '<br>pouvez dépenser avec Klarna peut augmenter.<br><br>Exemples de plans<br><br>Payer en 3 fois<br>Payer la totalité<br><br>Comment ça marche?',
    steps: [
      'Au moment du paiement, choisissez Klarna',
      "Choisissez votre plan de paiement<br>Différents plans de paiement peuvent être proposés en fonction du montant de<br>l'achat et de votre solvabilité. <br>",
      'Terminez votre commande <br>Le montant sera débité en fonction du plan de paiement que vous avez choisi.',
    ],
    legal: [
      "Un acompte peut être requis. Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager. Les échéanciers de paiement sont soumis à des conditions d'éligibilité. Consultez les <a style='text-decoration: underline;' href='https://www.klarna.com/fr/legal/' target='_blank'>conditions générales</a>.",
    ],
  },
};
export const DE_BNPL_PRODUCT_INFO: Record<string, BnplProductInfo> = {
  paypal: {
    key: 'paypal',
    label: 'PayPal',
    logo: '/payment/PC_paypal.png',
    title: 'Später bezahlen in monatlichen Raten',
    legal: [
      "Vorbehaltlich Kreditwürdigkeitsprüfung. Ab 99 € und bis zu 10.000 € Bestellwert. Laufzeiten von 3, 6, 12 oder 24 Monaten. Bei Beträgen über 5.000 € stehen Laufzeiten von 12 oder 24 Monaten zur Verfügung. Der Kreditgeber ist PayPal (Europe) S.à r.l. et Cie, S.C.A., 22-24 Boulevard Royal, L-2449 Luxembourg. Nutzungsvoraussetzung ist ein deutsches PayPal-Privatkonto mit einer verknüpften Debitkarte oder einem Bankkonto mit gültigem SEPA-Lastschriftmandat als Zahlungsquelle. Es gelten die <a style='text-decoration: underline;' href='https://www.paypal.com/de/digital-wallet/ratenzahlung/terms' target='_blank'>Bedingungen</a>.PayPal bietet Kunden in anderen Ländern ähnliche Produkte an. Produktmerkmale und grenzüberschreitende Verfügbarkeit sind je nach Markt unterschiedlich. Weitere Einzelheiten finden Sie in den jeweiligen Produktbedingungen.",
    ],
  },
  klarna: {
    key: 'klarna',
    label: 'Klarna',
    logo: '/payment/PC_klarna.png',
    title: 'Flexibel und sicher zahlen mit Klarna',
    subTitle: 'Entscheide selbst, wie du bezahlen willst',
    description:
      "<br><br>Deine möglichen Zahlungsoptionen<br><br>6 Raten<br>Sofort bezahlen <br>In bis zu 30 Tagen bezahlen<br><br>So funktioniert's:",
    steps: [
      'Wähle Klarna am Checkout',
      'Entscheide dich für einen Zahlungsplan. Je nach Kaufbetrag und Kreditwürdigkeit werden dir möglicherweise andere Zahlungspläne angezeigt.',
      'Schließe deine Bestellung ab. Wie du bezahlst, hängt von deinem Zahlungsplan ab',
    ],
    legal: [
      'Alle Zahlungspläne setzte eine vorherige Kreditwürdigkeitsprüfung voraus.',
      "*Die Ratenzahlung erfolgt mit einem effektiven Jahreszins (APR) von 13,27%. Es gelten die <a style='text-decoration: underline;' href='https://www.klarna.com/de/agb/' target='_blank'>AGB</a>.",
    ],
  },
};

export const BNPL_PRODUCT_INFO_BY_SITE: Record<
  BnplLearnMoreSite,
  Record<string, BnplProductInfo>
> = {
  ca: CA_BNPL_PRODUCT_INFO,
  uk: UK_BNPL_PRODUCT_INFO,
  fr: FR_BNPL_PRODUCT_INFO,
  de: DE_BNPL_PRODUCT_INFO,
};

export const DEFAULT_BNPL_PRODUCT_INFO = CA_BNPL_PRODUCT_INFO;
